/* iter-116 — Pacer task collaboration panel.
 *
 * Surfaces inside PacerTaskDialog. Adds (per user spec):
 *   • Status pill picker         (editor / admin / engineer only)
 *   • Threaded notes (Q1-b)      (any signed-in user)
 *   • Pacer-style checklist      (any signed-in user — Q2-a)
 *   • File attachments (Q3-b)    (any signed-in user)
 *
 * Pure data-in / data-out: parent passes `task` + onChange callback.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckSquare, Square, Trash2, Paperclip, Send, Plus, Loader2, Sparkles,
} from 'lucide-react';
import FilePreviewThumb from './FilePreviewThumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EDITOR_ROLES = new Set(['admin', 'engineer', 'editor']);
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'On Hold', 'Blocked', 'Completed', 'Cancelled'];
const STATUS_COLORS = {
  'Not Started': { bg: '#E5E7EB', fg: '#374151' },
  'In Progress': { bg: '#FEF3C7', fg: '#92400E' },
  'On Hold':     { bg: '#E0E7FF', fg: '#4338CA' },
  'Blocked':     { bg: '#FEE2E2', fg: '#B91C1C' },
  'Completed':   { bg: '#DCFCE7', fg: '#15803D' },
  'Cancelled':   { bg: '#F3F4F6', fg: '#6B7280' },
};

function authHeaders() {
  const t = (typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null) || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}
function currentRole() {
  try { return (typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : ''); } catch { return ''; }
}
function currentEmail() {
  try { return (typeof window !== 'undefined' ? (localStorage.getItem('user_email') || '') : ''); } catch { return ''; }
}
function fmtAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusEditor({ task, accent, onUpdated }) {
  const role = currentRole();
  const canEdit = EDITOR_ROLES.has(role);
  const [busy, setBusy] = useState(false);
  const change = async (newStatus) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/pacer/tasks/${task.id}/status`,
        { status: newStatus, progress_percent: newStatus === 'Completed' ? 100 : (task.progress_percent ?? null) },
        { headers: authHeaders() }
      );
      toast.success(`Status → ${newStatus}`);
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status change failed');
    } finally {
      setBusy(false);
    }
  };
  if (!canEdit) {
    const c = STATUS_COLORS[task.status] || STATUS_COLORS['Not Started'];
    return (
      <span className="text-[11px] font-bold px-2 py-1 rounded-md" style={{ background: c.bg, color: c.fg }}>
        {task.status || 'Not Started'}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid="pacer-task-status-editor">
      {STATUS_OPTIONS.map((s) => {
        const active = (task.status || 'Not Started') === s;
        const c = STATUS_COLORS[s];
        return (
          <button
            key={s}
            type="button"
            disabled={busy}
            onClick={() => !active && change(s)}
            className={`text-[10px] font-bold px-2 py-1 rounded-md transition ${active ? '' : 'opacity-50 hover:opacity-100'}`}
            style={{ background: active ? c.bg : '#fff', color: c.fg, border: `1px solid ${active ? c.fg : '#E5E7EB'}` }}
            data-testid={`pacer-task-status-opt-${s.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

function NotesPanel({ task, accent, onChanged }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const me = currentEmail();
  const role = currentRole();
  const canModerate = EDITOR_ROLES.has(role);
  const notes = (task.notes || []).slice().sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pacer/tasks/${task.id}/notes`, { body: text }, { headers: authHeaders() });
      onChanged?.({ ...task, notes: [...notes, data] });
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add note');
    } finally { setBusy(false); }
  };
  const remove = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/pacer/tasks/${task.id}/notes/${n.id}`, { headers: authHeaders() });
      onChanged?.({ ...task, notes: notes.filter((x) => x.id !== n.id) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  return (
    <div data-testid="pacer-task-notes-panel">
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase mb-1.5" style={{ color: accent, fontFamily: 'Manrope' }}>
        Notes · {notes.length}
      </div>
      <ul className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1 mb-2">
        {notes.length === 0 && (
          <li className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No notes yet — add the first below.</li>
        )}
        {notes.map((n) => {
          const canDelete = n.author === me || canModerate;
          return (
            <li key={n.id} className="rounded-md px-2 py-1.5 group" style={{ background: '#F4F1EB' }} data-testid={`pacer-task-note-${n.id}`}>
              <div className="flex items-center gap-2 text-[10px]" style={{ color: '#6B7280' }}>
                <span className="font-bold" style={{ color: '#1A2E2E' }}>{n.author || '—'}</span>
                <span>·</span>
                <span>{fmtAt(n.at)}</span>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => remove(n)}
                    className="ml-auto opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50 p-0.5 rounded"
                    aria-label="Delete note"
                    data-testid={`pacer-task-note-delete-${n.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="text-[12px] whitespace-pre-wrap" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                {n.body}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Add a note…"
          className="flex-1 text-[12px] rounded-md border px-2 py-1.5"
          style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
          data-testid="pacer-task-note-input"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !draft.trim()}
          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md text-white disabled:opacity-50"
          style={{ background: accent, fontFamily: 'Manrope' }}
          data-testid="pacer-task-note-submit-btn"
        >
          <Send className="w-3 h-3" /> Post
        </button>
      </div>
    </div>
  );
}

function StepsPanel({ task, accent, onChanged }) {
  const [draft, setDraft] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const me = currentEmail();
  const role = currentRole();
  const canModerate = EDITOR_ROLES.has(role);
  const steps = (task.steps || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const done = steps.filter((s) => s.done).length;
  const aiGenerate = async () => {
    if (!canModerate) {
      toast.error('Editor / Admin / Engineer only');
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/pacer/tasks/${task.id}/ai-checklist`,
        {},
        { headers: authHeaders(), timeout: 90000 }
      );
      toast.success(`AI added ${data.created} step${data.created === 1 ? '' : 's'}`);
      onChanged?.({ ...task, steps: [...steps, ...(data.steps || [])] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI checklist failed');
    } finally {
      setAiBusy(false);
    }
  };
  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      const { data } = await axios.post(`${API}/pacer/tasks/${task.id}/steps`, { text }, { headers: authHeaders() });
      onChanged?.({ ...task, steps: [...steps, data] });
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add step');
    }
  };
  const toggle = async (s) => {
    try {
      const { data } = await axios.patch(`${API}/pacer/tasks/${task.id}/steps/${s.id}`, { done: !s.done }, { headers: authHeaders() });
      onChanged?.({ ...task, steps: steps.map((x) => (x.id === s.id ? data : x)) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };
  const rename = async (s, text) => {
    const newText = (text || '').trim();
    if (!newText || newText === s.text) return;
    try {
      const { data } = await axios.patch(`${API}/pacer/tasks/${task.id}/steps/${s.id}`, { text: newText }, { headers: authHeaders() });
      onChanged?.({ ...task, steps: steps.map((x) => (x.id === s.id ? data : x)) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete step "${s.text}"?`)) return;
    try {
      await axios.delete(`${API}/pacer/tasks/${task.id}/steps/${s.id}`, { headers: authHeaders() });
      onChanged?.({ ...task, steps: steps.filter((x) => x.id !== s.id) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  return (
    <div data-testid="pacer-task-steps-panel">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: accent, fontFamily: 'Manrope' }}>
          Steps · {done}/{steps.length}
        </div>
        {steps.length > 0 && (
          <div className="flex-1 mx-3 h-1 rounded" style={{ background: '#E5E7EB' }}>
            <div className="h-1 rounded" style={{ background: accent, width: `${steps.length ? (done / steps.length) * 100 : 0}%` }} />
          </div>
        )}
        {canModerate && (
          <button
            type="button"
            onClick={aiGenerate}
            disabled={aiBusy}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md disabled:opacity-50"
            style={{ background: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
            data-testid="pacer-task-step-ai-btn"
            title="Generate checklist with AI (Claude)"
          >
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            AI generate
          </button>
        )}
      </div>
      <ul className="space-y-1 max-h-[180px] overflow-y-auto pr-1 mb-2">
        {steps.length === 0 && (
          <li className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No steps yet. Add the first below.</li>
        )}
        {steps.map((s) => {
          const canDelete = s.by === me || canModerate;
          return (
            <li key={s.id} className="flex items-start gap-2 group" data-testid={`pacer-task-step-${s.id}`}>
              <button
                type="button"
                onClick={() => toggle(s)}
                className="mt-0.5"
                aria-label={s.done ? 'Mark step as not done' : 'Mark step as done'}
                data-testid={`pacer-task-step-toggle-${s.id}`}
              >
                {s.done
                  ? <CheckSquare className="w-4 h-4" style={{ color: accent }} />
                  : <Square className="w-4 h-4 text-gray-400" />}
              </button>
              <span
                className={`flex-1 text-[12px] ${s.done ? 'line-through opacity-60' : ''} cursor-text`}
                style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => rename(s, e.currentTarget.textContent)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                data-testid={`pacer-task-step-text-${s.id}`}
              >
                {s.text}
              </span>
              <span className="text-[10px] italic" style={{ color: '#9CA3AF' }}>
                {s.done && s.done_by ? `done by ${s.done_by}` : `by ${s.by}`}
              </span>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => remove(s)}
                  className="opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50 p-0.5 rounded"
                  aria-label="Delete step"
                  data-testid={`pacer-task-step-delete-${s.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder="Add a step (e.g. Inspect HVAC)…"
          className="flex-1 text-[12px] rounded-md border px-2 py-1.5"
          style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
          data-testid="pacer-task-step-input"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md text-white disabled:opacity-50"
          style={{ background: accent, fontFamily: 'Manrope' }}
          data-testid="pacer-task-step-add-btn"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
}

function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 ** 2).toFixed(1)} MB`;
}

function absoluteUrl(u) {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

function AttachmentsPanel({ task, accent, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const atts = task.attachments || [];
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File exceeds 25MB limit');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/pacer/tasks/${task.id}/attachments`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onChanged?.({ ...task, attachments: [...atts, data] });
      toast.success('Document uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete attachment "${a.filename}"?`)) return;
    try {
      await axios.delete(`${API}/pacer/tasks/${task.id}/attachments/${a.id}`, { headers: authHeaders() });
      onChanged?.({ ...task, attachments: atts.filter((x) => x.id !== a.id) });
      toast.success('Attachment deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  return (
    <div data-testid="pacer-task-attachments-panel">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: accent, fontFamily: 'Manrope' }}>
          Documents · {atts.length}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md disabled:opacity-50"
          style={{ background: accent, color: 'white', fontFamily: 'Manrope' }}
          data-testid="pacer-task-attach-upload-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
          Upload
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.txt,.md"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="pacer-task-attach-input"
        />
      </div>
      {atts.length === 0 && (
        <p className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No documents yet.</p>
      )}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {atts.map((a) => (
          <li key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md border group"
              style={{ borderColor: '#EAE5D9', background: 'white' }}
              data-testid={`pacer-task-attach-${a.id}`}>
            <FilePreviewThumb url={a.url} filename={a.filename} size="md" color={accent} />
            <div className="flex-1 min-w-0">
              <a
                href={absoluteUrl(a.url)}
                target="_blank"
                rel="noreferrer noopener"
                className="block truncate text-[12px] font-bold hover:underline"
                style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
              >
                {a.filename}
              </a>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>
                {fmtSize(a.size_bytes)} · by {a.by}
              </span>
            </div>
            <button
              type="button"
              onClick={() => remove(a)}
              className="opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50 p-1 rounded flex-shrink-0"
              aria-label="Delete attachment"
              data-testid={`pacer-task-attach-delete-${a.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PacerTaskCollab({ task, accent, onChanged }) {
  if (!task) return null;
  return (
    <div className="mt-4 pt-4 border-t space-y-4" style={{ borderColor: '#E5E7EB' }}>
      <div>
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase mb-1.5" style={{ color: accent, fontFamily: 'Manrope' }}>
          Status
        </div>
        <StatusEditor task={task} accent={accent} onUpdated={onChanged} />
      </div>
      <StepsPanel task={task} accent={accent} onChanged={onChanged} />
      <NotesPanel task={task} accent={accent} onChanged={onChanged} />
      <AttachmentsPanel task={task} accent={accent} onChanged={onChanged} />
    </div>
  );
}
